import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    faBuilding,
    faCarBuilding, faCity, faCloud, faClouds, faHospital, faHospitalAlt, faHospitals, faHotel,
    faIndustryAlt, faSmoke,
    faStore,
    faStoreAlt, faTree, faTreeAlt,
    faWarehouse,
    faWarehouseAlt
} from '@fortawesome/pro-solid-svg-icons';
import { faCarSide, faTruck, faTruckPickup } from '@fortawesome/pro-solid-svg-icons';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { bufferCount, combineAll, distinct, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { animate, sequence, state, style, transition, trigger } from '@angular/animations';
import { ElementObserverService } from '../../shared/services/element-observer.service';
import { IconEnum } from '../../../enum/icon.enum';
import { Icon, IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AsyncPipe } from '@angular/common';

interface Sprite {
    left: string;
    bottom: string;
    color: string;
    size: SizeProp;
    icon: IconProp;
}

@Component({
    selector: 'app-quote-progress',
    templateUrl: './quote-progress.component.html',
    styleUrls: ['./quote-progress.component.scss'],
    animations: [
        trigger('progress', [
            state('inactive', style({
                height: '0px',
                opacity: 0,
                display: 'none'
            })),
            state('busy', style({
                height: '140px',
                opacity: 1,
                display: 'block'
            })),
            state('dormant', style({
                height: '20px',
                opacity: 0,
                display: 'block'
            })),
            transition('inactive => busy', animate('1s ease')),
            transition('busy => dormant', animate('1s ease')),
            transition('* => inactive', animate('0s')) // no animation if hidden
        ]),
        trigger('backgroundFast', [
            state('start', style({
                left: 0
            })),
            state('end', style({
                left: '-3000px'
            })),
            transition('start => end', [
                animate('20s')
            ]),
            transition('* => start', [
                animate('0s')
            ]),
            transition('* => dormant', [
                animate('0.3s')
            ]),
        ]),
        trigger('backgroundSlow', [
            state('start', style({
                left: 0
            })),
            state('end', style({
                left: '-3000px'
            })),
            transition('start => end', [
                animate('40s')
            ]),
            transition('* => start', [
                animate('0s')
            ]),
            transition('* => dormant', [
                animate('0.3s')
            ])
        ])
    ],
    imports: [FontAwesomeModule, AsyncPipe]
})
export class QuoteProgressComponent implements OnInit, OnDestroy, AfterViewInit {

    LAYER_WIDTH = 4500;

    public background = { fast: 'start', slow: 'start' };

    private buildingIcons: { icon: IconProp, size: SizeProp, bottom: number, width: number }[] = [
        { icon: faTree, size: '2x', bottom: -1, width: 32 },
        { icon: faTreeAlt, size: '2x', bottom: -1, width: 32 },
        { icon: faStore, size: '2x', bottom: -1, width: 32 },
        { icon: faStoreAlt, size: '2x', bottom: -1, width: 32 },
        { icon: faBuilding, size: '5x', bottom: 0, width: 72 },
        { icon: faWarehouseAlt, size: '3x', bottom: 0, width: 48 },
        { icon: faWarehouse, size: '3x', bottom: 0, width: 48 },
        { icon: faIndustryAlt, size: '4x', bottom: -3, width: 64 },
        { icon: faHotel, size: '4x', bottom: 0, width: 64 },
        { icon: faHospital, size: '4x', bottom: 0, width: 64 },
        { icon: faHospitalAlt, size: '4x', bottom: 0, width: 64 },
        { icon: faCity, size: '5x', bottom: 0, width: 72 }
    ];

    private cloudIcons = [
        faCloud,
        faClouds,
        faSmoke
    ];

    truckIcon = faTruck;

    private colorCloudShades = [
        'primary-color-50', 'primary-color-100'
    ];

    private colorBuildingShades = [
        'primary-color-100', 'primary-color-200', 'primary-color-300',
        'primary-color-400'
    ];

    lastRandomElement: number = null;

    sprites: { buildings: Sprite[], clouds: Sprite[] } = {
        buildings: [],
        clouds: []
    };

    @Input()
    progress$: Observable<number>;

    @Input() busy: boolean;
    @Input() started: boolean;

    get progressState(): 'busy' | 'dormant' | 'inactive' {
        if (!this.started) { return 'inactive'; }
        return this.busy ? 'busy' : 'dormant';
    }

    @ViewChild('quoteProgress')
    quoteProgressElement: ElementRef;

    containerWidth = 600;
    containerWidth$: Observable<number>;
    containerWidthSubscription: Subscription;
    truckLeft$: Observable<string>;

    constructor(private elementObserver: ElementObserverService) {
        this.prepare();
    }

    ngOnInit(): void {
        const position$ = this.progress$.pipe(
            map((progress) => {
                if (this.busy && progress < 100) {
                    return (20 + ((this.containerWidth - 20) * (progress / 100)));
                } else if (progress === 100) {
                    return (this.containerWidth + 200);
                } else {
                    return (-200);
                }
            })
        );
        this.truckLeft$ = position$.pipe(
            map((position) => position + 'px')
        );
    }

    randomElement<P>(elements: P[]): P {
        let n;
        do {
            n = Math.floor(elements.length * Math.random());
        } while (n === this.lastRandomElement);
        this.lastRandomElement = n;
        return elements[n];
    }

    prepare(): void {

        this.sprites.clouds = [];
        const cloudCount = Math.ceil(Math.random() * 9);
        for (let i = 0; i < cloudCount; i++) {
            const x = this.LAYER_WIDTH * Math.random();
            this.sprites.clouds.push({
                left: x + 'px',
                icon: this.randomElement<IconProp>(this.cloudIcons),
                color: this.randomElement<string>(this.colorCloudShades),
                bottom: null,
                size: null
            });
        }

        this.sprites.buildings = [];
        let left = 0;
        while (left < this.LAYER_WIDTH) {
            const icon = this.randomElement(this.buildingIcons);
            const color = this.randomElement(this.colorBuildingShades);
            this.sprites.buildings.push({
                left: left + 'px',
                icon: icon.icon,
                color,
                size: icon.size,
                bottom: icon.bottom + 'px'
            });
            left += icon.width;
        }
    }

    animationBackgroundDone(layer: string): void {
        this.background[layer] = this.background[layer] === 'end' ? 'start' : 'end';
    }

    animationState(layer: string): string {
        return this.busy ? this.background[layer] : 'dormant';
    }

    ngAfterViewInit(): void {
        this.containerWidth$ = this.elementObserver.observeWidth(this.quoteProgressElement);
        setTimeout(() => {
            this.containerWidthSubscription = this.containerWidth$.subscribe((width) => this.containerWidth = width);
        });
    }

    ngOnDestroy(): void {
        if (this.containerWidthSubscription) {
            this.containerWidthSubscription.unsubscribe();
        }
    }
}
